import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import { getKaajSectionSpacing } from "../../../../modules/layout/kaaj-section-spacing";
import type {
  ITeamGridBlockAction,
  ITeamGridBlockMember,
  ITeamGridBlockProps,
  ITeamGridBlockSocialLink,
} from "./TeamGridBlock.props";
import { RichTextHtml } from "../../RichTextHtml";

const Image = resolveKaajPrimitive("Image");
const Link = resolveKaajPrimitive("Link");

const sectionThemeClass = "text-[#151927]";

const tone = {
  eyebrow: "border-[#CAD7FF] bg-white text-[#2144FF] shadow-[0_10px_26px_rgba(33,68,255,0.09)]",
  subheading: "text-[#4B5568]",
  card: "border-[#D9E1FF] bg-white text-[#151927] shadow-[0_12px_36px_rgba(33,68,255,0.07)] hover:border-[#BFD0FF] hover:shadow-[0_22px_58px_rgba(33,68,255,0.12)]",
  imageShell: "border-[#D9E1FF] bg-[#FAFBFF]",
  imageInner: "bg-[#FAFBFF]",
  cardTitle: "text-[#151927]",
  role: "text-[#2144FF]",
  body: "text-[#4B5568]",
  meta: "text-[#5D6678]",
  divider: "border-[#D9E1FF]",
  contact:
    "border-[#D9E1FF] bg-white text-[#2144FF] hover:border-[#2144FF] hover:bg-[#F6F1FF] hover:text-[#132EDB]",
  contactPrimary:
    "border-[#2144FF] bg-[#2144FF] text-white shadow-[0_12px_28px_rgba(33,68,255,0.18)] hover:bg-[#132EDB]",
  contactLink: "text-[#2144FF] decoration-[#2144FF]/30 hover:text-[#132EDB]",
  focusOffset: "focus-visible:ring-offset-white",
};

type TeamGridTone = typeof tone;

const ActionLink = ({ action, tone }: { action: ITeamGridBlockAction; tone: TeamGridTone }) => {
  if (!action.href || !action.text) {
    return null;
  }

  const isPrimary = action.style === "primary";

  return (
    <Link
      aria-label={action.ariaLabel || action.text}
      className={cn(
        "inline-flex min-h-11 items-center justify-center rounded-full px-5 py-3 text-center text-sm font-extrabold transition-[background-color,border-color,box-shadow,color,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2144FF] focus-visible:ring-offset-4 motion-reduce:transition-none motion-safe:hover:-translate-y-0.5 max-[420px]:w-full",
        isPrimary ? tone.contactPrimary : tone.contact,
        tone.focusOffset,
      )}
      href={action.href}
      rel={
        action.target === "_blank" || action.href.startsWith("http")
          ? "noopener noreferrer"
          : undefined
      }
      target={action.target}
    >
      {action.text}
      {isPrimary ? (
        <span aria-hidden="true" className="ml-2">
          →
        </span>
      ) : null}
    </Link>
  );
};

const ContactLink = ({
  ariaLabel,
  href,
  label,
  target,
  tone,
}: {
  ariaLabel?: string | null;
  href?: string | null;
  label?: string | null;
  target?: string | null;
  tone: TeamGridTone;
}) => {
  if (!href || !label) {
    return null;
  }

  return (
    <Link
      aria-label={ariaLabel || label}
      className={cn(
        "inline-flex max-w-full items-center text-sm font-semibold leading-6 underline decoration-current/30 underline-offset-4 transition-colors duration-200 [overflow-wrap:anywhere] hover:decoration-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2144FF] focus-visible:ring-offset-4 motion-reduce:transition-none",
        tone.contactLink,
        tone.focusOffset,
      )}
      href={href}
      rel={target === "_blank" || href.startsWith("http") ? "noopener noreferrer" : undefined}
      target={target ?? undefined}
    >
      {label}
    </Link>
  );
};

const SocialLink = ({ link, tone }: { link: ITeamGridBlockSocialLink; tone: TeamGridTone }) => (
  <ContactLink
    ariaLabel={link.ariaLabel}
    href={link.href}
    label={link.platform}
    target={link.target}
    tone={tone}
  />
);

const TeamMemberCard = ({
  index,
  member,
  tone,
}: {
  index: number;
  member: ITeamGridBlockMember;
  tone: TeamGridTone;
}) => {
  const imageSrc = member.image?.defaultSrc;
  const imagePriority = index < 3;

  return (
    <article
      className={cn(
        "group/card flex h-full min-w-0 flex-col overflow-hidden rounded-[1.5rem] border p-2.5 transition-[border-color,background-color,box-shadow,transform] duration-300 motion-reduce:transition-none motion-safe:hover:-translate-y-0.5 sm:p-3",
        tone.card,
      )}
    >
      {imageSrc ? (
        <figure
          className={cn("relative overflow-hidden rounded-[1.15rem] border p-1.5", tone.imageShell)}
        >
          <div className={cn("relative overflow-hidden rounded-[0.85rem]", tone.imageInner)}>
            <Image
              alt={member.image?.alternateText ?? ""}
              className="aspect-[3/4] w-full object-cover object-[center_24%] transition duration-500 motion-reduce:transition-none motion-safe:group-hover/card:scale-[1.025]"
              decoding="async"
              height={member.image?.height ?? undefined}
              loading={imagePriority ? "eager" : "lazy"}
              priority={imagePriority}
              sizes={
                member.image?.sizes ?? "(min-width: 1280px) 24rem, (min-width: 768px) 42vw, 92vw"
              }
              src={imageSrc}
              srcSet={member.image?.srcSet ?? undefined}
              style={
                member.image?.focalPoint
                  ? {
                      objectPosition: `${member.image.focalPoint.x}% ${member.image.focalPoint.y}%`,
                    }
                  : undefined
              }
              width={member.image?.width ?? undefined}
            />
          </div>
        </figure>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col px-3 pb-3 pt-5 sm:px-4 sm:pb-4">
        <div className="space-y-2">
          {member.name ? (
            <h3
              className={cn(
                "text-balance text-2xl font-extrabold leading-[1.05] tracking-[-0.04em] [overflow-wrap:anywhere]",
                tone.cardTitle,
              )}
            >
              {member.name}
            </h3>
          ) : null}
          {member.role ? (
            <p
              className={cn("text-sm font-extrabold leading-6 [overflow-wrap:anywhere]", tone.role)}
            >
              {member.role}
            </p>
          ) : null}
        </div>

        {member.department || member.location || member.pronouns ? (
          <dl className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-xs font-extrabold uppercase leading-5 tracking-[0.14em]">
            {member.department ? (
              <div className="flex min-w-0 items-center gap-3">
                <dt className="sr-only">Department</dt>
                <dd className={cn("[overflow-wrap:anywhere]", tone.meta)}>{member.department}</dd>
              </div>
            ) : null}
            {member.location ? (
              <div className="flex min-w-0 items-center gap-3">
                <dt className="sr-only">Location</dt>
                <dd className={cn("[overflow-wrap:anywhere]", tone.meta)}>{member.location}</dd>
              </div>
            ) : null}
            {member.pronouns ? (
              <div className="flex min-w-0 items-center gap-3">
                <dt className="sr-only">Pronouns</dt>
                <dd className={cn("[overflow-wrap:anywhere]", tone.meta)}>{member.pronouns}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}

        {member.shortBio ? (
          <p
            className={cn("mt-4 text-pretty text-sm leading-6 [overflow-wrap:anywhere]", tone.body)}
          >
            {member.shortBio}
          </p>
        ) : null}

        {member.email || member.phone || member.socialLinks?.length ? (
          <div className={cn("mt-auto flex flex-wrap gap-x-4 gap-y-2 border-t pt-4", tone.divider)}>
            <ContactLink
              href={member.email ? `mailto:${member.email}` : undefined}
              label={member.email}
              tone={tone}
            />
            <ContactLink
              href={member.phone ? `tel:${member.phone.replace(/[^+\d]/g, "")}` : undefined}
              label={member.phone}
              tone={tone}
            />
            {member.socialLinks?.map((link, linkIndex) => (
              <SocialLink key={`${link.platform}-${linkIndex}`} link={link} tone={tone} />
            ))}
          </div>
        ) : null}
      </div>
    </article>
  );
};

export const TeamGridBlock = ({
  anchorId,
  buttons,
  eyebrow,
  heading,
  members,
  parentClass,
  spacing,
  subheading,
}: ITeamGridBlockProps) => {
  const actionItems = buttons ?? [];
  const memberItems = members ?? [];
  const hasIntro = Boolean(eyebrow || heading || subheading || actionItems.length > 0);

  if (!hasIntro && memberItems.length === 0) {
    return null;
  }

  return (
    <section
      aria-labelledby={heading && anchorId ? `${anchorId}-heading` : undefined}
      className={cn(
        "relative isolate overflow-hidden",
        sectionThemeClass,
        getKaajSectionSpacing(spacing),
        parentClass,
      )}
      id={anchorId ?? undefined}
    >
      <div className="kaaj-container relative">
        {hasIntro ? (
          <div className="mb-10 flex flex-col gap-5 lg:mb-14">
            <div className="min-w-0">
              {eyebrow ? (
                <p
                  className={cn(
                    "mb-4 inline-flex w-fit rounded-full border px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.18em] [overflow-wrap:anywhere]",
                    tone.eyebrow,
                  )}
                >
                  {eyebrow}
                </p>
              ) : null}
              {heading ? (
                <h2
                  className="kaaj-section-heading"
                  id={anchorId ? `${anchorId}-heading` : undefined}
                >
                  {heading}
                </h2>
              ) : null}
              {subheading ? (
                <RichTextHtml
                  className={cn(
                    "mt-5 text-pretty text-base leading-8 [overflow-wrap:anywhere] sm:text-lg",
                    tone.subheading,
                  )}
                  html={subheading}
                />
              ) : null}
              {actionItems.length > 0 ? (
                <div className="mt-7 flex flex-wrap gap-3 max-[420px]:flex-col max-[420px]:items-stretch">
                  {actionItems.map((action, index) => (
                    <ActionLink action={action} key={`${action.href}-${index}`} tone={tone} />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {memberItems.length > 0 ? (
          <ul className="grid list-none gap-5 p-0 md:grid-cols-2 lg:gap-6 xl:grid-cols-3">
            {memberItems.map((member, index) => (
              <li className="min-w-0" key={`${member.name || member.role || "member"}-${index}`}>
                <TeamMemberCard index={index} member={member} tone={tone} />
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
};

TeamGridBlock.displayName = "Block::TeamGridBlock";
